import {
  Button,
  InlineNotification,
  Stack,
  StructuredListBody,
  StructuredListCell,
  StructuredListRow,
  StructuredListWrapper,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@carbon/react';
import { Locked, Logout, TrashCan, Unlocked, View } from '@carbon/icons-react';
import {
  asProblem,
  hasPermission,
  ListTable,
  PageHeader,
  problemMessage,
  StatusTag,
  useAdmin,
  useFormat,
} from '@pos/admin-ui';
import { ConfirmDialog } from '@pos/design-system';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router';
import { ErrorNotice } from '../common/Notices';
import {
  ACCOUNTS_ERASE,
  ACCOUNTS_MANAGE,
  ACCOUNTS_REVEAL,
  accountKey,
  eventsKey,
  getAccount,
  listEvents,
  revealContact,
  revealNetwork,
  sendCommand,
  STATUS_TAG,
  type AccountCommand,
  type AccountStatus,
  type CustomerAccountEvent,
} from './customerAccountsApi';

/**
 * One website or app account (ADR 0082): profile with contact masked (Reveal is audited), its devices and activity; lock, unlock,
 * sign out everywhere, clear a password lockout or erase, each with its own permission and a confirmation. A command is applied online
 * within seconds and shows here once applied.
 */
export function CustomerAccountPage() {
  const { t } = useTranslation();
  const { dateTime } = useFormat();
  const { session } = useAdmin();
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const account = useQuery({ queryKey: accountKey(id), queryFn: () => getAccount(id), refetchInterval: 5_000 });
  const events = useInfiniteQuery({
    queryKey: eventsKey(id),
    queryFn: ({ pageParam }) => listEvents(id, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.hasMore ? last.items[last.items.length - 1]?.at : undefined),
    refetchInterval: 5_000,
  });
  const [confirm, setConfirm] = useState<AccountCommand | null>(null);
  const [sent, setSent] = useState<AccountCommand | null>(null);
  const reveal = useMutation({ mutationFn: () => revealContact(id) });
  // The whole IP and user agent of the events loaded so far, a page at a time as listed (ADR 0123); audited at HO.
  const network = useMutation({
    mutationFn: async () => {
      const pages = events.data?.pages ?? [];
      const befores = pages.map((_, i) => (i === 0 ? undefined : pages[i - 1]?.items.at(-1)?.at));
      const revealed = await Promise.all(befores.map((before) => revealNetwork(id, before)));
      return new Map(revealed.flat().map((n) => [n.id, n]));
    },
  });
  const command = useMutation({
    mutationFn: (c: AccountCommand) => sendCommand(id, c),
    onSuccess: (_, c) => {
      setSent(c);
      setConfirm(null);
      void queryClient.invalidateQueries({ queryKey: accountKey(id) });
    },
  });

  if (account.error)
    return <ErrorNotice title={problemMessage(t, asProblem(account.error))} onRetry={() => void account.refetch()} />;
  if (!account.data) return null;
  const a = account.data;
  const status = a.status as AccountStatus;
  const canManage = hasPermission(session, ACCOUNTS_MANAGE) && status !== 'erased';
  const locked = a.passwordLockedUntil != null;
  const eventLabel = (e: CustomerAccountEvent) =>
    t(`customerAccounts.events.${e.type}` as I18nKey, { defaultValue: e.type });
  const actor = (e: CustomerAccountEvent) => t(`customerAccounts.actor.${e.actor as 'customer' | 'staff' | 'system'}`);
  const allEvents = events.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <section className="pos-admin-page">
      <PageHeader
        title={a.name ?? a.maskedPhone ?? t('customerAccounts.unnamed')}
        description={t('customerAccounts.detailIntro')}
        actions={
          <>
            {hasPermission(session, ACCOUNTS_REVEAL) && status !== 'erased' && (
              <Button kind="tertiary" renderIcon={View} disabled={reveal.isPending} onClick={() => reveal.mutate()}>
                {t('customerAccounts.reveal')}
              </Button>
            )}
            {canManage &&
              (status === 'locked' ? (
                <Button kind="tertiary" renderIcon={Unlocked} onClick={() => setConfirm('unlock')}>
                  {t('customerAccounts.commands.unlock')}
                </Button>
              ) : (
                <Button kind="tertiary" renderIcon={Locked} onClick={() => setConfirm('lock')}>
                  {t('customerAccounts.commands.lock')}
                </Button>
              ))}
            {canManage && (
              <Button kind="tertiary" renderIcon={Logout} onClick={() => setConfirm('sign_out')}>
                {t('customerAccounts.commands.sign_out')}
              </Button>
            )}
            {canManage && locked && (
              <Button kind="tertiary" renderIcon={Unlocked} onClick={() => setConfirm('clear_lockout')}>
                {t('customerAccounts.commands.clear_lockout')}
              </Button>
            )}
            {hasPermission(session, ACCOUNTS_ERASE) && status !== 'erased' && (
              <Button kind="danger--tertiary" renderIcon={TrashCan} onClick={() => setConfirm('erase')}>
                {t('customerAccounts.commands.erase')}
              </Button>
            )}
          </>
        }
      />
      <Stack gap={6}>
        {sent && (
          <InlineNotification
            kind="info"
            lowContrast
            role="status"
            title={t('customerAccounts.sent')}
            onClose={() => setSent(null)}
          />
        )}
        {reveal.data && (
          <InlineNotification
            kind="warning"
            lowContrast
            title={t('customerAccounts.revealed')}
            subtitle={[reveal.data.phone, reveal.data.email].filter(Boolean).join(' · ')}
            onClose={() => reveal.reset()}
          />
        )}
        {reveal.error && <ErrorNotice title={problemMessage(t, asProblem(reveal.error))} />}
        <StructuredListWrapper isCondensed aria-label={t('customerAccounts.profile')}>
          <StructuredListBody>
            {(
              [
                [
                  'status',
                  <StatusTag key="s" status={STATUS_TAG[status] ?? 'inactive'}>
                    {t(`customerAccounts.status.${status}`)}
                  </StatusTag>,
                ],
                ['phone', a.maskedPhone ?? '—'],
                ['email', a.maskedEmail ?? '—'],
                ['member', a.memberAccountNo ?? '—'],
                [
                  'password',
                  a.hasPassword
                    ? locked
                      ? t('customerAccounts.passwordLocked', { until: dateTime(a.passwordLockedUntil ?? '') })
                      : t('customerAccounts.passwordSet')
                    : t('customerAccounts.passwordNone'),
                ],
                ['createdAt', dateTime(a.createdAt)],
                ['lastLogin', a.lastLoginAt ? dateTime(a.lastLoginAt) : '—'],
              ] as const
            ).map(([key, value]) => (
              <StructuredListRow key={key}>
                <StructuredListCell noWrap>{t(`customerAccounts.fields.${key}`)}</StructuredListCell>
                <StructuredListCell>{value}</StructuredListCell>
              </StructuredListRow>
            ))}
          </StructuredListBody>
        </StructuredListWrapper>
        <Tabs>
          <TabList aria-label={t('customerAccounts.title')} contained>
            <Tab>{t('customerAccounts.devices')}</Tab>
            <Tab>{t('customerAccounts.activity')}</Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              <ListTable
                ariaLabel={t('customerAccounts.devices')}
                rows={a.devices.map((d) => ({ ...d, id: d.sessionId }))}
                empty={{ heading: t('customerAccounts.noDevices') }}
                columns={[
                  {
                    key: 'device',
                    header: t('customerAccounts.columns.device'),
                    render: (d) => d.device ?? t('customerAccounts.unknownDevice'),
                  },
                  { key: 'ipPrefix', header: t('customerAccounts.columns.network'), render: (d) => d.ipPrefix ?? '—' },
                  { key: 'channel', header: t('customerAccounts.columns.channel'), render: (d) => d.channel },
                  {
                    key: 'signedInAt',
                    header: t('customerAccounts.columns.signedIn'),
                    render: (d) => dateTime(d.signedInAt),
                  },
                  {
                    key: 'lastUsedAt',
                    header: t('customerAccounts.columns.lastUsed'),
                    render: (d) => dateTime(d.lastUsedAt),
                  },
                ]}
              />
            </TabPanel>
            <TabPanel>
              <Stack gap={4}>
                {hasPermission(session, ACCOUNTS_REVEAL) && !network.data && allEvents.length > 0 && (
                  <div>
                    <Button
                      kind="tertiary"
                      renderIcon={View}
                      disabled={network.isPending}
                      onClick={() => network.mutate()}
                    >
                      {t('customerAccounts.revealNetwork')}
                    </Button>
                  </div>
                )}
                {network.error && <ErrorNotice title={problemMessage(t, asProblem(network.error))} />}
                <ListTable<CustomerAccountEvent>
                  ariaLabel={t('customerAccounts.activity')}
                  loading={events.isLoading}
                  error={events.error ? problemMessage(t, asProblem(events.error)) : undefined}
                  onRetry={() => void events.refetch()}
                  rows={allEvents}
                  empty={{ heading: t('customerAccounts.noActivity') }}
                  columns={[
                    { key: 'at', header: t('customerAccounts.columns.when'), render: (e) => dateTime(e.at) },
                    { key: 'type', header: t('customerAccounts.columns.event'), render: eventLabel },
                    { key: 'actor', header: t('customerAccounts.columns.actor'), render: actor },
                    { key: 'device', header: t('customerAccounts.columns.device'), render: (e) => e.device ?? '—' },
                    {
                      key: 'ipPrefix',
                      header: t('customerAccounts.columns.network'),
                      render: (e) => network.data?.get(e.id)?.ip ?? e.ipPrefix ?? '—',
                    },
                    ...(network.data
                      ? [
                          {
                            key: 'userAgent',
                            header: t('customerAccounts.columns.userAgent'),
                            render: (e: CustomerAccountEvent) => network.data.get(e.id)?.userAgent ?? '—',
                          },
                        ]
                      : []),
                    {
                      key: 'detail',
                      header: t('customerAccounts.columns.detail'),
                      render: (e) =>
                        e.detail
                          ? Object.entries(e.detail)
                              .map(([k, v]) => `${k}: ${v}`)
                              .join(' · ')
                          : '',
                    },
                  ]}
                />
                {events.hasNextPage && (
                  <div>
                    <Button
                      kind="ghost"
                      disabled={events.isFetchingNextPage}
                      onClick={() => void events.fetchNextPage()}
                    >
                      {t('customerAccounts.more')}
                    </Button>
                  </div>
                )}
              </Stack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Stack>
      {confirm && (
        <ConfirmDialog
          open
          danger={confirm === 'erase' || confirm === 'lock'}
          title={t(`customerAccounts.confirm.${confirm}.title`)}
          confirmLabel={t(`customerAccounts.commands.${confirm}`)}
          busy={command.isPending}
          onConfirm={() => command.mutate(confirm)}
          onCancel={() => {
            setConfirm(null);
            command.reset();
          }}
        >
          <p>{t(`customerAccounts.confirm.${confirm}.text`)}</p>
          {command.error && <ErrorNotice title={problemMessage(t, asProblem(command.error))} />}
        </ConfirmDialog>
      )}
    </section>
  );
}
